import {
  useTranslate, TextField, TopToolbar,
  SimpleForm, required,
  useRecordContext,
  Button,
  Confirm,
  useSaveContext,
  useDataProvider,
  useRefresh,
  useNotify,
  useResourceContext,
  useRedirect, Create,
  type DataProvider,
  useGetList,
  useListContext,
  WrapperField,
} from 'react-admin';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import {
  Typography, TableCell,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ListLive } from '@react-admin/ra-realtime';
import { Datagrid, IfCanAccess } from '@react-admin/ra-rbac';
import { CertificationCategory, ExpiryTimeGranularityType } from '@x-guard/xgac-types/xgac';
import SearchFields from '../components/fields/Filters';
import { LIST_DEBOUNCE } from '../../../config';
import { TimeGranularityField } from '../../../components/fields/TimeGranularityField';
import { EditWithSuccess } from '../../../components/baseForms/EditWithSuccess';
import { GrayBooleanSwitchWithUpperLabel } from '../components/inputs/GrayBooleanSwitchWithUpperLabel';
import { GrayExpiryTimeGranularityInputWithUpperLabel } from '../components/inputs/GrayExpiryTimeGranularityInputWithUpperLabel';
import { GrayTextInputWithUpperLabel } from '../components/inputs/GrayTextInputWithUpperLabel';
import { CertifiedUsersButton } from '../../../components/buttons/CertifiedUsersButton';
import { AssetsInDialogButton } from '../../../components/buttons/assetsInDialogButton';
import { CustomReminderPeriodInput } from '../components/inputs/CustomReminderPeriodInput';
import { NotifiedPeopleFormValue, NotifiedPeopleInput } from '../components/inputs/NotifiedPeopleInput';
import TextFieldNullable from '../../../components/fields/TextFieldNullable';
import { DEFAULT_REMINDER_PERIODS } from '../../../utils/certificationsAndItemsExpiry';
import { xgacDataProvider } from '../../../dataProviders/xgacDataProvider';
import { CreateFromPresetDialog, CategoryPresetKey } from '../components/CreateFromPresetDialog';
import {
  AddCertifiedUsersModal,
  type CertifiedUsersDraft,
  type CertifiedUsersCategory,
} from '../components/AddCertifiedUsersModal';
import { AddCertifiedUsersRowButton } from '../components/AddCertifiedUsersRowButton';
import {
  AssigneeChoice, buildCertifiedUsersAssigneeContext,
  CertifiedUsersAssigneeContext,
  userCertificationTransformer,
} from '../../../utils/certifiedUsersAssignments';
import { notifyWithError } from '../../../utils/errorHandling';
import {
  BhvkBackButton,
  BhvkSaveButton,
  BhvkTableDeleteButton,
  BhvkTableEditButton,
} from '../buttons/ActionButtons';
import { PageContentCard } from '../cards/PageContentCard';
import { FormToolbar } from '../components/FormToolbar';

export type CertificationCategoryPresetKey =
  | 'basic_emergency_response_officer'
  | 'bhv_team_leader'
  | 'head_of_emergency_response'
  | 'basic_vca'
  | 'vol_vca'
  | 'forklift_certificate'
  | 'reach_truck_certificate'
  | 'aerial_work_platform_certificate'
  | 'nen_3140_skilled_person'
  | 'nen_3140_installation_responsible'
  | 'respiratory_protection'
  | 'working_at_height'
  | 'basic_first_aid'
  | 'pediatric_first_aid'
  | 'sports_first_aid'
  | 'first_aid_refresher'
  | 'cpr_aed';

export type CertificationCategoryPreset = {
  presetKey: CertificationCategoryPresetKey;
  validityPeriod: CertificationCategory['validityPeriod'];
};

const PRESET_I18N_PREFIX = 'resources.certification-categories.presets';

const certificationPresets: CertificationCategoryPreset[] = [
  {
    presetKey: 'basic_emergency_response_officer',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'bhv_team_leader',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'head_of_emergency_response',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'basic_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'vol_vca',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 10 },
  },
  {
    presetKey: 'forklift_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'reach_truck_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'aerial_work_platform_certificate',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'nen_3140_skilled_person',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'nen_3140_installation_responsible',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 3 },
  },
  {
    presetKey: 'respiratory_protection',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'working_at_height',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 5 },
  },
  {
    presetKey: 'basic_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'pediatric_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'sports_first_aid',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 2 },
  },
  {
    presetKey: 'first_aid_refresher',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
  {
    presetKey: 'cpr_aed',
    validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
  },
];

const presetToCreatePayload = (
  preset: CertificationCategoryPreset,
  translate: (key: string) => string,
  notifiedPeople?: NotifiedPeopleFormValue,
  isDocumentaryProofRequired = true,
): Omit<CertificationCategory, '_id' | 'customer' | 'share' | 'createdAt' | 'updatedAt'> => ({

  name: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.name`),
  description: translate(`${PRESET_I18N_PREFIX}.${preset.presetKey}.description`),
  validityPeriod: preset.validityPeriod,
  reminderPeriods: DEFAULT_REMINDER_PERIODS,
  notifiedPeople: {
    peopleToNotify: (notifiedPeople?.peopleToNotify ?? []).map((personId) => ({
      _id: personId,
      _ref: 'Asset',
    })),
    isCertificationHolderNotified: notifiedPeople?.isCertificationHolderNotified ?? true,
    emails: notifiedPeople?.emails ?? [],
  },
  isDocumentaryProofRequired,

});

const buildNotifiedPeoplePayload = (notifiedPeople: NotifiedPeopleFormValue) => ({
  peopleToNotify: (notifiedPeople.peopleToNotify ?? []).map((personId) => ({
    _id: personId,
    _ref: 'Asset',
  })),
  isCertificationHolderNotified: notifiedPeople.isCertificationHolderNotified ?? true,
  emails: notifiedPeople.emails ?? [],
});

const updateCategoriesNotifiedPeople = async (
  categories: CertifiedUsersCategory[],
  notifiedPeople: NotifiedPeopleFormValue,
  dataProvider: DataProvider,
  resource: string | undefined,
) => Promise.all(
  categories.map((category) => {

    if (!category.id) return Promise.resolve();

    return dataProvider.update(resource || 'certificates', {
      id: category.id,
      data: {
        notifiedPeople: buildNotifiedPeoplePayload(notifiedPeople),
      },
      previousData: { id: category.id },
    });

  }),
);

const createCategoriesFromPresets = async (
  selectedPresetKeys: CategoryPresetKey[],
  notifiedPeople: NotifiedPeopleFormValue,
  dataProvider: DataProvider,
  resource: string | undefined,
  translate: (key: string) => string,
  documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
): Promise<CertifiedUsersCategory[]> => {

  const selected = certificationPresets.filter((row) => selectedPresetKeys.includes(row.presetKey));

  return Promise.all(
    selected.map((item) => dataProvider
      .create(resource || 'certificates', {
        data: presetToCreatePayload(item, translate, notifiedPeople, documentaryProofByPresetKey?.[item.presetKey] ?? true),
      })
      .then(({ data }) => ({
        id: data.id,
        name: data.name,
        validityPeriod: data.validityPeriod,
        isDocumentaryProofRequired: data.isDocumentaryProofRequired,
      }))),
  );

};

const presetKeysToCertifiedUsersCategories = (
  selectedPresetKeys: CategoryPresetKey[],
  translate: (key: string) => string,
  documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
): CertifiedUsersCategory[] => (
  certificationPresets
    .filter((p) => selectedPresetKeys.includes(p.presetKey))
    .map((p) => ({
      name: translate(`${PRESET_I18N_PREFIX}.${p.presetKey}.name`),
      validityPeriod: p.validityPeriod,
      isDocumentaryProofRequired: documentaryProofByPresetKey?.[p.presetKey] ?? true,
    }))
);

type PresetStep = 'presets' | 'certifiedUsers' | 'peopleToNotify';

const CertificationCreateFromPresetsDialog = (props: {
  open: boolean;
  onClose: () => void;
  onAddCustom: () => void;
  choices: AssigneeChoice[];
  isChoicesLoading?: boolean;
}) => {

  const [existingCategoryNames, setExistingCategoryNames] = useState<string[]>([]);
  const [wizardStep, setWizardStep] = useState<PresetStep>('presets');
  const [pendingSelection, setPendingSelection] = useState<{
    selectedPresetKeys: CategoryPresetKey[];
    documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>;
  } | null>(null);
  const [certifiedUsersDraft, setCertifiedUsersDraft] = useState<CertifiedUsersDraft | null>(null);
  const [createdCategoriesFromSubmit, setCreatedCategoriesFromSubmit] = useState<
  CertifiedUsersCategory[] | null
>(null);
  const [notifiedPeopleDraft, setNotifiedPeopleDraft] = useState<NotifiedPeopleFormValue | null>(null);
  const [certifiedUsersFormInstanceKey, setCertifiedUsersFormInstanceKey] = useState(0);
  const [peopleFormInstanceKey, setPeopleFormInstanceKey] = useState(0);
  const [modalCategories, setModalCategories] = useState<CertifiedUsersCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dataProvider = useDataProvider();
  const resource = useResourceContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const existingNamesSet = useMemo(() => new Set(existingCategoryNames), [existingCategoryNames]);
  const presetDialogOpen = props.open && (wizardStep === 'presets' || wizardStep === 'peopleToNotify');
  const certifiedUsersModalOpen = props.open && wizardStep === 'certifiedUsers';

  const resetWizard = () => {

    setWizardStep('presets');
    setPendingSelection(null);
    setCertifiedUsersDraft(null);
    setNotifiedPeopleDraft(null);
    setModalCategories([]);
    setCertifiedUsersFormInstanceKey(0);
    setCreatedCategoriesFromSubmit(null);

  };

  const handleAbortWizard = () => {

    resetWizard();
    props.onClose();

  };

  useEffect(() => {

    if (!props.open) {

      resetWizard();

      return;

    }

    xgacDataProvider.getList('certificates', {
      pagination: { page: 1, perPage: -1 },
    }).then(({ data }) => {

      setExistingCategoryNames(data.map((row) => row.name));

    });

  }, [props.open]);

  const isSamePresetSelection = (
    previous: {
      selectedPresetKeys: CategoryPresetKey[];
      documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>;
    } | null,
    selectedPresetKeys: CategoryPresetKey[],
    documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
  ): boolean => {

    if (!previous) return false;
    if (previous.selectedPresetKeys.length !== selectedPresetKeys.length) return false;

    return selectedPresetKeys.every((key, index) => {

      if (previous.selectedPresetKeys[index] !== key) return false;

      const previousPdfRequired = previous.documentaryProofByPresetKey?.[key] ?? true;
      const nextPdfRequired = documentaryProofByPresetKey?.[key] ?? true;
      return previousPdfRequired === nextPdfRequired;

    });

  };

  const handlePresetsContinueToCertifiedUsers = (
    selectedPresetKeys: CategoryPresetKey[],
    documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
  ) => {

    const sameSelection = isSamePresetSelection(
      pendingSelection,
      selectedPresetKeys,
      documentaryProofByPresetKey,
    );

    if (!sameSelection) {

      setCertifiedUsersDraft(null);
      setCreatedCategoriesFromSubmit(null);

    }

    setPendingSelection({ selectedPresetKeys, documentaryProofByPresetKey });
    setModalCategories(
      presetKeysToCertifiedUsersCategories(
        selectedPresetKeys,
        translate,
        documentaryProofByPresetKey,
      ),
    );
    setCertifiedUsersFormInstanceKey((key) => key + 1);
    setWizardStep('certifiedUsers');

  };

  const handleCertifiedUsersContinue = (draft: CertifiedUsersDraft) => {

    setCertifiedUsersDraft(draft);
    setPeopleFormInstanceKey((key) => key + 1);
    setWizardStep('peopleToNotify');

  };

  const handleCertifiedUsersBack = () => {

    setWizardStep('presets');

  };

  const handleBackFromPeople = () => {

    setCertifiedUsersFormInstanceKey((key) => key + 1);
    setWizardStep('certifiedUsers');

  };

  const handlePeopleSubmit = async (
    selectedPresetKeys: CategoryPresetKey[],
    notifiedPeople: NotifiedPeopleFormValue,
    documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
  ) => {

    if (selectedPresetKeys.length === 0) return;
    setIsLoading(true);
    try {

      let created = createdCategoriesFromSubmit;

      if (!created) {

        created = await createCategoriesFromPresets(
          selectedPresetKeys,
          notifiedPeople,
          dataProvider,
          resource,
          translate,
          documentaryProofByPresetKey,
        );
        setCreatedCategoriesFromSubmit(created);

      } else {

        await updateCategoriesNotifiedPeople(
          created,
          notifiedPeople,
          dataProvider,
          resource,
        );

      }
      if (selectedPresetKeys.length === 1 && certifiedUsersDraft) {

        const categoryId = created[0]?.id;
        if (categoryId) {

          await Promise.all(
            certifiedUsersDraft.recipients
              .filter((r) => certifiedUsersDraft.newRecipientIds.includes(r.id))
              .map((r) => {

                const details = certifiedUsersDraft.certificationsByAssetId[r.id];
                if (!details) return Promise.resolve();
                return dataProvider.create('user-certifications', {
                  data: userCertificationTransformer({
                    certificationCategory: categoryId,
                    firstIssuedOn: details.firstIssuedOn,
                    lastReceivedOn: details.lastReceivedOn,
                    expiryDate: details.expiryDate,
                    plannedRenewalDate: null,
                    ...(details.document?.rawFile ? { document: details.document } : {}),
                  }),
                  meta: { assetId: r.id },
                });

              }),
          );

        }

      }
      notify(
        `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`,
        { type: 'success' },
      );
      refresh();
      handleAbortWizard();

    } catch (error) {

      notifyWithError(notify, error);

    } finally {

      setIsLoading(false);

    }

  };

  return (
    <>
      <CreateFromPresetDialog
        isSubmitting={isLoading}
        open={presetDialogOpen}
        step={wizardStep === 'peopleToNotify' ? 'peopleToNotify' : 'presets'}
        onStepChange={(step) => {

          if (step === 'peopleToNotify') {

            setPeopleFormInstanceKey((key) => key + 1);

          }
          setWizardStep(step);

        }}
        onClose={handleAbortWizard}
        onSubmit={handlePeopleSubmit}
        onPresetsContinueToCertifiedUsers={handlePresetsContinueToCertifiedUsers}
        onBackFromPeople={handleBackFromPeople}
        initialSelectedPresetKeys={pendingSelection?.selectedPresetKeys}
        initialDocumentaryProofByPresetKey={pendingSelection?.documentaryProofByPresetKey}
        presets={certificationPresets}
        existingNamesSet={existingNamesSet}
        onAddCustom={props.onAddCustom}
        showCertifiedUserOption
        showCertifiedUsersStep
        presetPrefix={PRESET_I18N_PREFIX}
        showDocumentaryProofToggles
        peopleDefaultValues={notifiedPeopleDraft ?? undefined}
        peopleFormInstanceKey={peopleFormInstanceKey}
        onPeopleStepLeave={setNotifiedPeopleDraft}
        i18n={{
          title: 'resources.certification-categories.text.add_from_presets_title',
          searchPlaceholder: 'resources.certification-categories.presets.text.search',
          noResults: 'resources.certification-categories.text.no_results',
          presetAlreadyExists: 'resources.certification-categories.text.preset_already_exists',
          peopleToNotifyTitle: 'resources.certification-categories.text.people_to_notify_title',
          peopleToNotifyLabel: 'resources.certification-categories.fields.people_to_notify',
          peopleToNotifyHelper: 'resources.certification-categories.text.people_to_notify_helper',
        }}
        renderExtraHeadCells={() => (
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {translate('resources.certification-categories.fields.validity_period')}
          </TableCell>
        )}
        renderExtraBodyCells={(row, textColor) => (
          <TableCell sx={{ color: textColor }}>
            {'validityPeriod' in row && translate(`general.text.time_granularity_types.${row.validityPeriod.type}`, { smart_count: row.validityPeriod.value })}
          </TableCell>
        )}
      />
      <AddCertifiedUsersModal
        open={certifiedUsersModalOpen}
        onClose={handleAbortWizard}
        categories={modalCategories}
        choices={props.choices}
        isChoicesLoading={props.isChoicesLoading}
        initialDraft={certifiedUsersDraft}
        onBack={handleCertifiedUsersBack}
        formInstanceKey={certifiedUsersFormInstanceKey}
        onStepLeave={setCertifiedUsersDraft}
        onContinue={handleCertifiedUsersContinue}
        allowEmptyConfirm
      />
    </>
  );

};

const CertificationDeleteButton = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  return (
    <BhvkTableDeleteButton
      mutationMode="pessimistic"
      confirmTitle={`${translate('ra.action.delete')} ${translate('resources.certification-categories.text.title_single').toLowerCase()} "${record?.name}"`}
    />
  );

};

const CertificationCategoriesDatagrid = (props: {
  onAssigneeContextChange: (context: CertifiedUsersAssigneeContext & { isLoading: boolean }) => void;
}) => {

  const { data: categories = [] } = useListContext();
  const { onAssigneeContextChange } = props;
  const translate = useTranslate();

  const { data: users = [], isLoading: isUsersLoading } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });
  const { data: groups = [], isLoading: isGroupsLoading } = useGetList('asset-groups', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });
  const [assigneeContext, setAssigneeContext] = useState<CertifiedUsersAssigneeContext | null>(null);
  const [isResolving, setIsResolving] = useState(true);

  const categoryIds = useMemo(
    () => categories.map((category) => String(category.id ?? category._id)),
    [categories],
  );

  useEffect(() => {

    if (isUsersLoading || isGroupsLoading) {

      onAssigneeContextChange({
        baseChoices: [],
        certifiedAssetIdsByCategoryId: {},
        assetsByCategoryId: {},
        isLoading: true,
      });
      return undefined;

    }

    let cancelled = false;

    setIsResolving(true);
    onAssigneeContextChange({
      baseChoices: [],
      certifiedAssetIdsByCategoryId: {},
      assetsByCategoryId: {},
      isLoading: true,
    });
    buildCertifiedUsersAssigneeContext(users, groups, categoryIds)
      .then((context) => {

        if (cancelled) return;
        setAssigneeContext(context);
        setIsResolving(false);
        onAssigneeContextChange({ ...context, isLoading: false });

      })
      .catch(() => {

        if (cancelled) return;
        setAssigneeContext(null);
        setIsResolving(false);
        onAssigneeContextChange({
          baseChoices: [],
          certifiedAssetIdsByCategoryId: {},
          assetsByCategoryId: {},
          isLoading: false,
        });

      });

    return () => {

      cancelled = true;

    };

  }, [categoryIds, users, groups, isUsersLoading, isGroupsLoading, onAssigneeContextChange]);

  const isLoading = isUsersLoading || isGroupsLoading || isResolving;

  return (
    <Datagrid
      rowClick={false}
      bulkActionButtons={false}
      sx={{
        '& .column-actions': {
          width: '1%',
          whiteSpace: 'nowrap',
          paddingLeft: 1,
          paddingRight: 1,
        },
      }}
    >
      <TextField source="name" label="general.fields.name"/>
      <CertifiedUsersButton label="resources.certification-categories.text.user_count"/>
      <TimeGranularityField source="validityPeriod" label="resources.certification-categories.fields.validity_period"/>
      <AssetsInDialogButton
        variant="bhvk"
        nameColumnLabel={translate('resources.certification-categories.fields.people_to_notify')}
        label="resources.certification-categories.fields.people_to_notify"
        resource="certificates"
        source="notifiedPeople.peopleToNotify"
        className="BhvkCompactContainedButton-root"
        closeClassName="BhvkCancelButton-root"
      />
      <TextFieldNullable source="description" label="general.fields.description"/>
      <WrapperField label="" sortable={false} source="actions">
        <Stack
          direction="row"
          alignItems="flex-start"
          spacing={2}
          sx={{ width: 'fit-content', py: 1 }}
        >
          <IfCanAccess action="edit" resource="user-certifications">
            <AddCertifiedUsersRowButton
              baseChoices={assigneeContext?.baseChoices ?? []}
              assetsByCategoryId={assigneeContext?.assetsByCategoryId ?? {}}
              certifiedAssetIdsByCategoryId={assigneeContext?.certifiedAssetIdsByCategoryId ?? {}}
              isChoicesLoading={isLoading}
              disabled={assigneeContext === null && !isLoading}
            />
          </IfCanAccess>
          <IfCanAccess action="edit">
            <BhvkTableEditButton/>
          </IfCanAccess>
          <IfCanAccess action="delete">
            <CertificationDeleteButton/>
          </IfCanAccess>
        </Stack>
      </WrapperField>
    </Datagrid>
  );

};

export const CertificationList = () => {

  const [createFromPresetsDialogOpen, setCreateFromPresetsDialogOpen] = useState(false);
  const [assigneeContext, setAssigneeContext] = useState<CertifiedUsersAssigneeContext & { isLoading: boolean } | null>(null);
  const redirect = useRedirect();
  const resource = useResourceContext();
  const translate = useTranslate();

  const handleAddCustom = () => {

    setCreateFromPresetsDialogOpen(false);
    redirect('create', resource);

  };

  return (
    <PageContentCard>
      <h3>{translate('resources.certification-categories.title')}</h3>
      <ListLive
        empty={false}
        filters={SearchFields} debounce={LIST_DEBOUNCE} title="resources.certification-categories.title" actions={
          <IfCanAccess action="create">
            <TopToolbar>
              <Button label="general.text.add" variant="contained" size="medium" startIcon={<AddIcon/>} onClick={() => setCreateFromPresetsDialogOpen(true)} />
            </TopToolbar>
          </IfCanAccess>
        }
        sx={{ '& > .RaList-main > .RaList-content': { mt: '12px' } }}
      >
        <CertificationCategoriesDatagrid onAssigneeContextChange={setAssigneeContext}/>
      </ListLive>
      <CertificationCreateFromPresetsDialog
        open={createFromPresetsDialogOpen}
        onClose={() => setCreateFromPresetsDialogOpen(false)}
        choices={assigneeContext?.baseChoices ?? []}
        isChoicesLoading={assigneeContext?.isLoading ?? false}
        onAddCustom={handleAddCustom}
      />
    </PageContentCard>
  );

};

const certificationTransformer = (data: any) => {

  return {
    ...data,
    notifiedPeople: {
      peopleToNotify: (data.notifiedPeople?.peopleToNotify ?? []).map((personId: string) => ({
        _id: personId,
        _ref: 'Asset',
      })),
      isCertificationHolderNotified: data.notifiedPeople?.isCertificationHolderNotified,
      emails: data.notifiedPeople?.emails ?? [],
    },
  };

};

const CertificationFields = () => {

  return (
    <Stack spacing={1.5} sx={{ width: '500px' }}>
      <GrayTextInputWithUpperLabel
        source="name"
        validate={required()}
        label="general.fields.name"
        helperText="resources.certification-categories.text.name_helper"
        isRequired
        fullWidth
      />
      <GrayTextInputWithUpperLabel
        source="description"
        label="general.fields.description"
        multiline rows={4}
        helperText={false}
        fullWidth
      />
      <GrayExpiryTimeGranularityInputWithUpperLabel
        label={'resources.certification-categories.fields.validity_period'}
        source="validityPeriod"
        helperText="resources.certification-categories.text.validity_period_helper"
        isRequired
      />
      <NotifiedPeopleInput
        isRequired
        showCertifiedUserOption
        label="resources.certification-categories.fields.people_to_notify"
        helperText="resources.certification-categories.text.people_to_notify_helper"
      />
      <CustomReminderPeriodInput
        helperText="resources.certification-categories.text.reminder_periods_helper"
        isRequired
      />
      <GrayBooleanSwitchWithUpperLabel
        source="isDocumentaryProofRequired"
        label="resources.certification-categories.fields.is_documentary_proof_required"
        helperText="resources.certification-categories.text.documentary_proof_helper"
      />
    </Stack>
  );

};

const CertificationEditHeading = () => {

  const record = useRecordContext();
  const translate = useTranslate();

  return (
    <>
      <h4 style={{ marginBottom: '10px' }}>{translate('general.text.edit')} {'"'}{record?.name}{'"'}</h4>
      <hr/>
    </>
  );

};

export const CertificationCreate = () => {

  const translate = useTranslate();
  const navigate = useNavigate();
  const redirect = useRedirect();
  const notify = useNotify();

  const [modalOpen, setModalOpen] = useState(false);
  const [createdCategory, setCreatedCategory] = useState<CertifiedUsersCategory | null>(null);

  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.saved').toLowerCase()}`;

  const handleModalClose = () => {

    setModalOpen(false);
    setCreatedCategory(null);
    redirect('/certificates');

  };

  return (
    <>
      <Create
        component={PageContentCard}
        title="resources.certification-categories.title"
        transform={certificationTransformer}
        redirect={false}
        mutationOptions={{
          onSuccess: (data) => {

            notify(toastLabel, { type: 'success' });
            setCreatedCategory({
              id: data.id,
              name: data.name,
              validityPeriod: data.validityPeriod,
              isDocumentaryProofRequired: data.isDocumentaryProofRequired,
            });
            setModalOpen(true);

          },
        }}
      >
        <h4>{translate('resources.certification-categories.text.create_title')}</h4>
        <Typography variant="body1">
          {translate('resources.certification-categories.text.category_explanation')}
        </Typography>
        <hr />
        <SimpleForm
          defaultValues={{
            validityPeriod: { type: ExpiryTimeGranularityType.Year, value: 1 },
            notifiedPeople: { peopleToNotify: [], emails: [], isCertificationHolderNotified: true },
            reminderPeriods: [{ type: ExpiryTimeGranularityType.Month, value: 1 }],
            isDocumentaryProofRequired: true,
          }}
          toolbar={
            <FormToolbar sx={{ maxWidth: '500px' }}>
              <BhvkBackButton onClick={() => navigate(-1)} />
              <BhvkSaveButton />
            </FormToolbar>
          } sx={{ maxWidth: '1000px' }}>
          <CertificationFields/>
        </SimpleForm>
      </Create>

      {createdCategory && (
        <AddCertifiedUsersModal
          open={modalOpen}
          onClose={handleModalClose}
          onComplete={handleModalClose}
          categories={[createdCategory]}
        />
      )}
    </>
  );

};

const SaveWithDocumentaryProofConfirm = () => {

  const translate = useTranslate();
  const record = useRecordContext();
  const { handleSubmit } = useFormContext();
  const { save } = useSaveContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const wasInitiallyRequired = useRef<boolean | undefined>();

  useEffect(() => {

    if (!record) {

      return;

    }
    wasInitiallyRequired.current = record.isDocumentaryProofRequired;

  }, [record]);

  const runSave = (values: Partial<CertificationCategory>) => {

    save?.(values);

  };
  const trySave = handleSubmit((values) => {

    if (
      wasInitiallyRequired.current
      && values.isDocumentaryProofRequired === false
    ) {

      setConfirmOpen(true);
      return;

    }
    runSave(values);

  });
  return (
    <>
      <BhvkSaveButton label="ra.action.save" onClick={trySave} />
      <Confirm
        isOpen={confirmOpen}
        title={translate('resources.certification-categories.text.edit_confirmation_dialog_title')}
        content={translate('resources.certification-categories.text.edit_confirmation_dialog_content')}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {

          setConfirmOpen(false);
          handleSubmit(runSave)();

        }}
      />
    </>
  );

};

export const CertificationEdit = () => {

  const navigate = useNavigate();
  const translate = useTranslate();
  const toastLabel = `${translate('resources.certification-categories.text.title_single')} ${translate('general.text.updated').toLowerCase()}`;

  return (
    <EditWithSuccess
      component={PageContentCard}
      title="resources.certification-categories.title"
      actions={false}
      toastLabel={toastLabel}
      transform={certificationTransformer}
    >
      <CertificationEditHeading/>
      <SimpleForm toolbar={
        <FormToolbar sx={{ maxWidth: '500px' }}>
          <BhvkBackButton onClick={() => navigate(-1)} />
          <SaveWithDocumentaryProofConfirm />
        </FormToolbar>}
      sx={{ maxWidth: '1000px' }}>
        <CertificationFields/>
      </SimpleForm>
    </EditWithSuccess>
  );

};
